import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { ListRegulation } from '@zelos/codegen/apis';
import Image from 'next/image';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRegulationModal } from './RegulationModal';
import RequiredAsterisk from '@/components/comm/RequiredAsterisk';
import { colors } from '@/constants/colors';
import icLaunch from '@/public/images/ic-launch.svg';

const Strong = styled.strong`
color: ${colors.black};
font-weight: 400;
`;

const Anchor = ({ children, url }: PropsWithChildren<{ url: string }>) => (
  <a
    css={css`
      display: inline-flex;
      text-decoration: underline;
    `}
    href={url}
    target="_blank"
    rel="noreferrer"
  >
    <span>{children}</span>
    <Image
      src={icLaunch}
      alt="launch"
      width={16}
      height={16}
    />
  </a>
);

const Openner = ({ children, content }: PropsWithChildren<{ content: string }>) => {
  const { showRegulation } = useRegulationModal();

  return (
    <span
      css={css`
        text-decoration: underline;
      `}
      onClick={() => {
        showRegulation(content);
      }}
    >{children}
    </span>
  );
};

type RegulationProps = {
  regulation: ListRegulation,
};

const Regulation = ({ regulation }: RegulationProps) => {
  const {
    isRequired,
    name,
    statement,
    title,
    url,
  } = regulation;

  const snippets = useMemo(
    () => title.split(new RegExp(`(${name})`)),
    [name, title],
  );

  return (
    <span>
      {!isRequired && <Strong>[선택]&nbsp;</Strong>}
      {snippets.map(
        (snippet) => snippet === name
          ? (
            <Strong key={uuidv4()}>{
              url && <Anchor url={url}>{snippet}</Anchor> ||
              statement.length > 1 && <Openner content={statement}>{snippet}</Openner> ||
              snippet
            }
            </Strong>
          )
          : <span key={uuidv4()}>{snippet}</span>,
      )}
      {isRequired && <RequiredAsterisk />}
    </span>
  );
};

export default Regulation;
